import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
} from '@mui/material';
import { SUFFIXES, RELATIONSHIP_CODES, US_STATES } from '../../InsuranceEditDialog/constants';
import type { InsuranceAddFormData } from '../hooks/useInsuranceAddForm';

type InsuredInfoField = Extract<keyof InsuranceAddFormData, 
  'insuredsFirstName' | 'insuredsMiddleName' | 'insuredsLastName' | 'insuredsSuffix' |
  'insuredsRelationship' | 'insuredsDateOfBirth' | 'insuredsStreet1' | 'insuredsStreet2' |
  'insuredsCity' | 'insuredsState' | 'insuredsZip'
>;

interface InsuredInfoSectionProps {
  sameAsPatient: boolean;
  insuredsFirstName: string;
  insuredsMiddleName: string;
  insuredsLastName: string;
  insuredsSuffix: string;
  insuredsRelationship: string;
  insuredsDateOfBirth: string | undefined;
  insuredsStreet1: string;
  insuredsStreet2: string;
  insuredsCity: string;
  insuredsState: string;
  insuredsZip: string;
  onSameAsPatientChange: (checked: boolean) => void;
  onInputChange: (field: InsuredInfoField, value: string) => void;
}

export const InsuredInfoSection: React.FC<InsuredInfoSectionProps> = ({
  sameAsPatient,
  insuredsFirstName,
  insuredsMiddleName,
  insuredsLastName,
  insuredsSuffix,
  insuredsRelationship,
  insuredsDateOfBirth,
  insuredsStreet1,
  insuredsStreet2,
  insuredsCity,
  insuredsState,
  insuredsZip,
  onSameAsPatientChange,
  onInputChange,
}) => (
  <Box>
    <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
        Insured's Information
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            checked={sameAsPatient}
            onChange={(e) => onSameAsPatientChange(e.target.checked)}
          />
        }
        label="Same as Patient"
        sx={{
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
            color: '#64748b'
          }
        }}
      />
    </Stack>
    {!sameAsPatient && (
      <Stack spacing={2}>
        {/* Name Row */}
        <Stack direction="row" spacing={2}>
          <TextField
            label="First Name"
            size="small"
            value={insuredsFirstName}
            onChange={(e) => onInputChange('insuredsFirstName', e.target.value)}
            disabled={sameAsPatient}
            sx={{
              flex: 1,
              '& .MuiInputBase-input': {
                backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
              }
            }}
            helperText={sameAsPatient ? 'Auto-filled from patient data' : ''}
          />
          <TextField
            label="Middle Name"
            size="small"
            value={insuredsMiddleName}
            onChange={(e) => onInputChange('insuredsMiddleName', e.target.value)}
            disabled={sameAsPatient}
            sx={{
              flex: 1,
              '& .MuiInputBase-input': {
                backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
              }
            }}
            helperText={sameAsPatient ? 'Auto-filled from patient data' : ''}
          />
          <TextField
            label="Last Name"
            size="small"
            value={insuredsLastName}
            onChange={(e) => onInputChange('insuredsLastName', e.target.value)}
            disabled={sameAsPatient}
            sx={{
              flex: 1,
              '& .MuiInputBase-input': {
                backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
              }
            }}
            helperText={sameAsPatient ? 'Auto-filled from patient data' : ''}
          />
          <FormControl sx={{ width: 120 }} size="small">
            <InputLabel>Suffix</InputLabel>
            <Select
              value={insuredsSuffix}
              onChange={(e) => onInputChange('insuredsSuffix', e.target.value)}
              disabled={sameAsPatient}
              label="Suffix"
              sx={{
                '& .MuiInputBase-input': {
                  backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
                }
              }}
            >
              <MenuItem value="">None</MenuItem>
              {SUFFIXES.map((suffix) => (
                <MenuItem key={suffix} value={suffix}>
                  {suffix}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Relationship and DOB Row */}
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ flex: 1 }} size="small">
            <InputLabel>Relationship</InputLabel>
            <Select
              value={insuredsRelationship || '09'}
              onChange={(e) => onInputChange('insuredsRelationship', e.target.value)}
              disabled={sameAsPatient}
              label="Relationship"
              sx={{
                '& .MuiInputBase-input': {
                  backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
                }
              }}
            >
              {RELATIONSHIP_CODES.map((rel) => (
                <MenuItem key={rel.code} value={rel.code}>
                  {rel.code} - {rel.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Date of Birth"
            type="date"
            size="small"
            value={insuredsDateOfBirth || ''}
            onChange={(e) => onInputChange('insuredsDateOfBirth', e.target.value)}
            disabled={sameAsPatient}
            sx={{
              flex: 1,
              '& .MuiInputBase-input': {
                backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
              }
            }}
            InputLabelProps={{ shrink: true }}
            helperText={sameAsPatient ? 'Auto-filled from patient data' : ''}
          />
        </Stack>

        {/* Address Row 1 */}
        <Stack direction="row" spacing={2}>
          <TextField
            label="Street Address 1"
            size="small"
            value={insuredsStreet1}
            onChange={(e) => onInputChange('insuredsStreet1', e.target.value)}
            disabled={sameAsPatient}
            sx={{
              flex: 2,
              '& .MuiInputBase-input': {
                backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
              }
            }}
            helperText={sameAsPatient ? 'Auto-filled from patient data' : ''}
          />
          <TextField
            label="Street Address 2"
            size="small"
            value={insuredsStreet2}
            onChange={(e) => onInputChange('insuredsStreet2', e.target.value)}
            disabled={sameAsPatient}
            sx={{
              flex: 2,
              '& .MuiInputBase-input': {
                backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
              }
            }}
            helperText={sameAsPatient ? 'Auto-filled from patient data' : ''}
          />
        </Stack>

        {/* City, State, Zip Row */}
        <Stack direction="row" spacing={2}>
          <TextField
            label="City"
            size="small"
            value={insuredsCity}
            onChange={(e) => onInputChange('insuredsCity', e.target.value)}
            disabled={sameAsPatient}
            sx={{
              flex: 2,
              '& .MuiInputBase-input': {
                backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
              }
            }}
            helperText={sameAsPatient ? 'Auto-filled from patient data' : ''}
          />
          <FormControl sx={{ flex: 1 }} size="small">
            <InputLabel>State</InputLabel>
            <Select
              value={insuredsState}
              onChange={(e) => onInputChange('insuredsState', e.target.value)}
              disabled={sameAsPatient}
              label="State"
              sx={{
                '& .MuiInputBase-input': {
                  backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
                }
              }}
            >
              <MenuItem value="">Select State</MenuItem>
              {US_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="ZIP Code"
            size="small"
            value={insuredsZip}
            onChange={(e) => onInputChange('insuredsZip', e.target.value)}
            disabled={sameAsPatient}
            sx={{
              flex: 1,
              '& .MuiInputBase-input': {
                backgroundColor: sameAsPatient ? '#f5f5f5' : 'inherit'
              }
            }}
            helperText={sameAsPatient ? 'Auto-filled from patient data' : ''}
          />
        </Stack>
      </Stack>
    )}
  </Box>
);
